import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../core/services/data/user.service';
import { IUser } from '../../types/datatypes';
import { UtilitiesService } from '../../core/services/utilities.service';

import { environment } from '../../../environments/environment';

interface IHandshakeMessage {
	app: string;
	handshake: true;
}

interface IAuthRequestMessage {
	app: string;
}

type AppMessage = IHandshakeMessage | IAuthRequestMessage;

@Component({
	selector: 'app-authorize-app',
	templateUrl: './authorize-app.component.html',
})
export class AuthorizeAppComponent implements OnInit, OnDestroy {
	public isLoading = true;

	public isError = false;

	public errorMessage = 'Something went wrong';

	public user: IUser | null = null;

	private processingAuthRequest = false;

	private messageHandler: (evt: MessageEvent) => void;

	constructor(
		private userService: UserService,
		private utilitiesService: UtilitiesService,
		private router: Router,
	) { }

	ngOnInit(): void {
		this.messageHandler = this.handleMessage.bind(this);
		window.addEventListener('message', this.messageHandler);
		this.userService.user$.subscribe({
			next: (user: IUser) => {
				/**
				 * Already authorized
				 */
				if (!this.isLoading) {
					return;
				}
				if (!user) {
					return;
				}
				this.user = user;
				if (!this.userService.isAdminOrSuperAdmin()) {
					this.isLoading = false;
					this.isError = true;
					this.errorMessage = 'Please login as an admin to authorize this app.';
				}
			},
			error: (_) => {
				this.isLoading = false;
				this.isError = true;
				this.errorMessage = 'Failed to load user data.';
			},
		});
	}

	ngOnDestroy(): void {
		window.removeEventListener('message', this.messageHandler);
	}

	getAppHost(app: string): string[] | null {
		switch (app) {
			case 'front-plugin':
				return [environment.frontPluginHost];
			default:
				return null;
		}
	}

	handleMessage(event: MessageEvent<AppMessage>) {
		if (!this.user) {
			return;
		}
		const app = event.data?.app;
		if (!app || typeof app !== 'string') {
			return;
		}

		const host = this.getAppHost(app);
		/**
		 * We don't want to give some random site our jwt token...
		 */
		if (!host.includes(event.origin)) {
			return;
		}

		if ('handshake' in event.data) {
			(event.source as Window)?.postMessage({ acknowledged: true }, event.origin);
		} else {
			/**
			 * Prevents multiple auth requests
			 */
			if (this.processingAuthRequest) {
				return;
			}
			this.processingAuthRequest = true;

			this.userService.authorizeApp({ app }).subscribe({
				next: (response) => {
					this.isLoading = false;
					if (!response.success) {
						this.isError = true;
						this.errorMessage = response.message;
						return;
					}
					(event.source as Window)?.postMessage({ jwt: response.jwt }, event.origin);
				},
				error: (_) => {
					this.isLoading = false;
					this.isError = true;
					this.errorMessage = 'Authorization failed.';
				},
			});
		}
	}

	login() {
		this.utilitiesService.saveRoute(window.location.pathname);
		this.router.navigate(['/login']);
	}
}
